import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Card, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { callService, agentService, phoneNumberService } from '../services/api';

const CreateCallPage = () => {
  const [formData, setFormData] = useState({
    agentId: '',
    fromNumber: '',
    toNumber: '',
    direction: 'outbound'
  });
  
  const [agents, setAgents] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resourcesLoading, setResourcesLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Fetch available agents and phone numbers
  useEffect(() => {
    const fetchResources = async () => {
      try {
        setResourcesLoading(true);
        
        // Fetch agents and phone numbers in parallel
        const [agentsData, phoneNumbersData] = await Promise.all([
          agentService.getAgents(),
          phoneNumberService.getActivePhoneNumbers()
        ]);
        
        setAgents(agentsData.agents || agentsData || []);
        setPhoneNumbers(phoneNumbersData);
        
        setResourcesLoading(false);
      } catch (error) {
        console.error('Failed to fetch resources:', error);
        setError('Failed to load agents and phone numbers. Please refresh the page.');
        setResourcesLoading(false);
      }
    };
    
    fetchResources();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      setLoading(true);
      
      const callData = {
        agentId: formData.agentId,
        phoneNumber: formData.toNumber,
        direction: formData.direction,
        fromNumber: formData.fromNumber
      };
      
      console.log('Creating call with data:', callData);
      
      const response = await callService.createCall(callData);
      console.log('Call created successfully:', response);
      
      // Navigate to calls page
      navigate('/calls');
    } catch (error) {
      console.error('Error creating call:', error);
      setError(error.response?.data?.message || 'Failed to create call. Please try again.');
      setLoading(false);
    }
  };

  const validateForm = () => {
    if (!formData.agentId) return 'Please select an agent';
    if (!formData.fromNumber) return 'Please select a from number';
    if (!formData.toNumber) return 'Please enter a to number';
    if (formData.direction === 'outbound' && !formData.fromNumber) {
      return 'Please select a from number for outbound calls';
    }
    return null;
  };

  const canSubmit = !validateForm() && !loading;

  return (
    <Container className="py-3">
      <Row className="align-items-center mb-4">
        <Col>
          <h1>Create Call</h1>
          <p className="text-muted">Make a new voice call with your AI agent</p>
        </Col>
        <Col className="text-end">
          <Button variant="outline-secondary" onClick={() => navigate('/calls')}>
            Back to Calls
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      {resourcesLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading agents and phone numbers...</p>
        </div>
      ) : (
        <Row>
          <Col md={8} className="mx-auto">
            <Card>
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Agent</Form.Label>
                    <Form.Select
                      name="agentId"
                      value={formData.agentId}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select an agent</option>
                      {agents.map((agent) => (
                        <option key={agent._id || agent.id} value={agent._id || agent.id}>
                          {agent.name}
                        </option>
                      ))}
                    </Form.Select>
                    {agents.length === 0 && (
                      <Form.Text className="text-muted">
                        No agents available. <a href="/agents/create">Create an agent first</a>.
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Call Direction</Form.Label>
                    <Form.Select
                      name="direction"
                      value={formData.direction}
                      onChange={handleChange}
                    >
                      <option value="outbound">Outbound (Call out)</option>
                      <option value="inbound">Inbound (Receive call)</option>
                    </Form.Select>
                  </Form.Group>

                  {formData.direction === 'outbound' && (
                    <Form.Group className="mb-3">
                      <Form.Label>From Number (Your Number)</Form.Label>
                      <Form.Select
                        name="fromNumber"
                        value={formData.fromNumber}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select your phone number</option>
                        {phoneNumbers.map((number) => (
                          <option key={number._id} value={number.phoneNumber}>
                            {number.phoneNumber} - {number.friendlyName}
                          </option>
                        ))}
                      </Form.Select>
                      {phoneNumbers.length === 0 && (
                        <Form.Text className="text-warning">
                          No phone numbers available. <a href="/phone-numbers">Add a phone number first</a>.
                        </Form.Text>
                      )}
                    </Form.Group>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Label>To Number (Recipient)</Form.Label>
                    <Form.Control
                      type="tel"
                      name="toNumber"
                      placeholder="+1234567890"
                      value={formData.toNumber}
                      onChange={handleChange}
                      required
                    />
                    <Form.Text className="text-muted">
                      Enter the phone number to call (E.164 format)
                    </Form.Text>
                  </Form.Group>

                  <div className="d-grid gap-2">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={!canSubmit}
                      className="mb-2"
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Creating Call...
                        </>
                      ) : (
                        'Create Call'
                      )}
                    </Button>
                    
                    {validateForm() && (
                      <Alert variant="warning" className="mb-0">
                        {validateForm()}
                      </Alert>
                    )}
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default CreateCallPage; 